import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/*
 * JOM 02-11-2026 — "Ver tudo": o lote seguinte entra 1500px antes do fim da lista (antes 100px); um pedido
 * falhado já não pára a lista; barra "Ver mais peças" (snippets/jom-grid-more.liquid); replaceState em vez de
 * pushState (o "voltar" sai da coleção); a página seguinte é lida das peças na grelha (os refs atrasam-se).
 */
const JOM_LOAD_AHEAD_PX = 1500;

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  /** JOM: true while the next page is being added, so the same page is never added twice. */
  #renderingNext = false;

  /** JOM: changes on every filter update; a page load started before it is dropped quietly. */
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    // JOM: "Ver mais peças" button
    this.addEventListener('click', this.#handleMoreClick);
    this.#updateMoreBar();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    this.removeEventListener('click', this.#handleMoreClick);
  }

  /**
   * JOM: loads the next page of items on demand ("Ver mais peças").
   * @returns {Promise<void>}
   */
  loadMore() {
    return this.#renderNextPage();
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          // JOM: start adding the next page well before the shopper reaches the end of the list.
          rootMargin: `100px 0px ${JOM_LOAD_AHEAD_PX}px 0px`,
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (error) {
      // JOM: a failed request must never leave the list waiting for ever.
      console.warn(`[JOM] A página ${page.page} da lista não carregou`, error);
    }

    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid || this.#renderingNext) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) {
      this.#updateMoreBar();
      return;
    }

    const generation = this.#generation;
    let appended = false;

    this.#renderingNext = true;
    this.#updateMoreBar('loading');

    try {
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;

        // The filters changed while this page was loading: the list was rebuilt, there is nothing to add.
        if (generation !== this.#generation) return;

        nextPageItemElements = this.#getGridForPage(nextPage.page);
      }

      if (nextPageItemElements?.length) {
        grid.append(...nextPageItemElements);

        this.#aspectRatioHelper?.processNewElements();

        history.replaceState(history.state, '', nextPage.url.toString());
        appended = true;
      }
    } finally {
      this.#renderingNext = false;
    }

    if (!appended) {
      this.#updateMoreBar('error');
      return;
    }

    this.#updateMoreBar();

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });

    // JOM: on tall screens the end of the list can still be in reach after a page is added; keep going.
    requestAnimationFrame(() => this.#continueIfNearEnd());
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    history.replaceState(history.state, '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    this.#updateMoreBar();

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * JOM: keeps loading while the end of the list is still within reach of the screen.
   */
  #continueIfNearEnd() {
    if (this.#renderingNext) return;

    const { grid, viewMoreNext } = this.refs;
    if (!grid || !viewMoreNext || !viewMoreNext.isConnected) return;

    // The list is hidden (for example replaced by the JOM guided search): nothing to do.
    if (!grid.getClientRects().length) return;

    const rect = viewMoreNext.getBoundingClientRect();
    if (rect.bottom >= -100 && rect.top <= window.innerHeight + JOM_LOAD_AHEAD_PX) {
      this.#renderNextPage();
    }
  }

  /**
   * JOM: the "Ver mais peças" link adds the next page in place. Without JavaScript it is a normal link.
   *
   * @param {MouseEvent} event
   */
  #handleMoreClick = (event) => {
    const { target } = event;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-jom-grid-button]');
    if (!button || !this.contains(button)) return;

    // Let the browser open the link in a new tab or window when asked to.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    event.preventDefault();
    this.loadMore();
  };

  /**
   * JOM: keeps the "Ver mais peças" bar (snippets/jom-grid-more.liquid) in step with the list.
   *
   * @param {'loading' | 'error'} [state]
   */
  #updateMoreBar(state) {
    const bar = this.querySelector('[data-jom-grid-more]');
    if (!(bar instanceof HTMLElement)) return;

    const total = Number(bar.dataset.total) || 0;
    const shown = this.#getCards().length;
    const nextPage = this.#getPage('next');
    const hasNext = Boolean(nextPage && this.#shouldUsePage(nextPage));
    const current = state ?? (hasNext ? 'idle' : shown >= total ? 'done' : 'end');

    /** @param {string | undefined} template */
    const fill = (template) =>
      (template ?? '').replace('[shown]', String(shown)).replace('[total]', String(total));

    bar.dataset.state = current;

    const count = bar.querySelector('[data-jom-grid-count]');
    if (count) {
      count.textContent = fill(current === 'done' ? bar.dataset.textAll : bar.dataset.textCount);
    }

    const button = bar.querySelector('[data-jom-grid-button]');
    if (button instanceof HTMLAnchorElement) {
      button.hidden = !hasNext;
      if (hasNext && nextPage) button.href = `${nextPage.url.pathname}${nextPage.url.search}`;
      button.textContent = fill(
        current === 'loading'
          ? bar.dataset.textLoading
          : current === 'error'
            ? bar.dataset.textError
            : bar.dataset.textMore
      );
      button.setAttribute('aria-busy', current === 'loading' ? 'true' : 'false');
    }
  }

  /**
   * JOM: the cards in the grid right now, read from the DOM (refs lag behind right after a page is added).
   * @returns {Element[]}
   */
  #getCards() {
    const { grid, cards } = this.refs;
    const inGrid = grid ? Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]')) : [];

    if (inGrid.length) return inGrid;

    return Array.isArray(cards) ? cards : [];
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const cards = this.#getCards();
    const isPrevious = type === 'previous';

    if (!cards.length) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!(targetCard instanceof HTMLElement)) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    // JOM: a page that was still loading belongs to the old list.
    this.#generation += 1;

    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');

        this.#updateMoreBar();
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}